import { Bell, Eye } from 'lucide-react';
import { formatNotificationTime, getNotificationColor } from '../../services/notificationsService';
import { getModuleByCode } from '../../services/modulesService';
import { getIconByName } from '../../utils/iconUtils';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Get module icon component based on module code (from DB)
 */
const getModuleIconComponent = (moduleCode) => {
    const moduleData = getModuleByCode(moduleCode);
    const iconName = moduleData?.icon || moduleData?.ICON || 'Bell';
    return getIconByName(iconName, Bell);
};

/**
 * Module colors mapping
 */
const MODULE_COLORS = {
    'MEETINGS': '#f43f5e',    // rose
    'BOOKS': '#f59e0b',       // amber
    'MESSAGES': '#8b5cf6',    // purple
    'CONTACTS': '#3b82f6',    // blue
    'CALENDAR': '#6366f1',    // indigo
    'EVENTS': '#ec4899',      // pink
    'PLACES': '#8b5cf6',      // purple
    'MAP': '#22c55e',         // green
    'AGENDA': '#f97316',      // orange
    'SUPPORT': '#06b6d4',     // cyan
    'WORK': '#f59e0b',        // amber
    'OFFERS': '#10b981',      // emerald
    'PRODUCTS': '#f43f5e',    // rose
    'KNOWLEDGE': '#a855f7',   // violet
    'EXPENSES': '#14b8a6',    // teal
    'MOMENTS': '#f59e0b',     // amber
    'CARPOOLING': '#22c55e',  // green
    'SYSTEM': '#6b7280'       // gray
};

/**
 * NotificationItem Component
 * Single notification row in the dropdown
 * @param {object} notification - Notification object
 * @param {function} onClick - Click handler for the whole item
 * @param {function} onMarkAsRead - Handler to mark as read (eye icon)
 * @param {boolean} showMarkAsRead - Whether to show the eye icon (default: true)
 */
const NotificationItem = ({ notification, onClick, onMarkAsRead, showMarkAsRead = true }) => {
    const { t } = useLanguage();

    // Handle both lowercase and uppercase keys (ColdFusion returns uppercase)
    const titleKey = notification.title || notification.TITLE;
    const messageKey = notification.message || notification.MESSAGE;
    const notificationType = notification.notificationType || notification.NOTIFICATIONTYPE;
    const moduleCode = notification.moduleCode || notification.MODULECODE;
    const senderFirstName = notification.senderFirstName || notification.SENDERFIRSTNAME;
    const senderLastName = notification.senderLastName || notification.SENDERLASTNAME;
    const senderName = senderFirstName ? `${senderFirstName} ${senderLastName || ''}`.trim() : '';
    const isRead = notification.isRead ?? notification.ISREAD;
    const createdAt = notification.createdAt || notification.CREATEDAT;

    // Parse metadata if available (for interpolation in translations)
    let metadata = {};
    const metadataRaw = notification.metadata || notification.METADATA;
    if (metadataRaw) {
        try {
            metadata = typeof metadataRaw === 'string' ? JSON.parse(metadataRaw) : metadataRaw;
        } catch (e) {
            console.warn('Failed to parse notification metadata:', e);
        }
    }
    // Add senderName to metadata for interpolation
    metadata.senderName = senderName || metadata.senderName || '';

    /**
     * Translate notification text with interpolation
     * If translation key exists, use it with metadata interpolation
     * Otherwise, fallback to raw text
     */
    const translateNotificationText = (key) => {
        if (!key) return '';

        // Get module-specific translations (e.g., t.notifications.books)
        const moduleTranslations = t.notifications?.books || {};

        // Check if key exists in translations
        let template = moduleTranslations[key];

        // If no translation found, return the raw key (might be legacy text)
        if (!template) {
            return key;
        }

        // Interpolate metadata values into the template
        // Replace {varName} with metadata.varName
        return template.replace(/\{(\w+)\}/g, (match, varName) => {
            return metadata[varName] !== undefined ? metadata[varName] : match;
        });
    };

    const title = translateNotificationText(titleKey);
    const message = translateNotificationText(messageKey);

    const ModuleIcon = getModuleIconComponent(moduleCode);
    const moduleColor = MODULE_COLORS[moduleCode] || '#6b7280';
    const isUnread = !isRead;

    const handleMarkAsRead = (e) => {
        e.stopPropagation();
        onMarkAsRead?.(notification);
    };

    return (
        <div
            onClick={onClick}
            className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer ${
                isUnread ? 'bg-blue-50/50' : ''
            }`}
        >
            <div className="flex gap-3">
                {/* Module Icon */}
                <div
                    className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${moduleColor}20` }}
                >
                    <ModuleIcon className="w-4 h-4" style={{ color: moduleColor }} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'} truncate`}>
                            {title}
                        </p>
                        {isUnread && showMarkAsRead && (
                            <button
                                onClick={handleMarkAsRead}
                                className="flex-shrink-0 p-1 rounded hover:bg-gray-200 transition-colors"
                                title="Marquer comme lu"
                            >
                                <Eye className="w-4 h-4 text-gray-400 hover:text-cabaza-purple" />
                            </button>
                        )}
                    </div>

                    {message && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {message}
                        </p>
                    )}

                    <div className="flex items-center gap-2 mt-1">
                        {senderName && (
                            <>
                                <span className="text-xs text-gray-400">{senderName}</span>
                                <span className="text-xs text-gray-300">•</span>
                            </>
                        )}
                        <span className="text-xs text-gray-400">
                            {formatNotificationTime(createdAt)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationItem;
